import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Column, ConfirmationModal, EnhancedAutocomplete, Typography, useOverlayContext } from 'seagull';
import { useCreateClientContact } from '~/hooks/reactQuery/queries/useClientMutations';
import { useLocaleKeys } from '~/i18n';
import { addToAnotherClientModalTextsPath, GetAddToAnotherClientModalTID } from './AddToAnotherClientModal.utils';
import { useAssociableClients } from './hooks/useAssociableClients';
const ADD_TO_ANOTHER_CLIENT_SUCCESS_TOAST_TEST_ID = 'add-to-another-client-success-toast';
export const AddToAnotherClientModal = (param)=>{
    let { contact, contacts, dismissModal, testId } = param;
    const { t } = useLocaleKeys(addToAnotherClientModalTextsPath);
    const { AddToAnotherClientModalTestIds } = useMemo(()=>GetAddToAnotherClientModalTID(testId), [
        testId
    ]);
    const { clients, isLoading, isError } = useAssociableClients({
        contact,
        contacts
    });
    const { createClientContact, isLoading: isCreating } = useCreateClientContact();
    const { showSuccessToast } = useOverlayContext();
    const [createFailed, setCreateFailed] = useState(false);
    const { control, handleSubmit, formState: { errors } } = useForm({
        mode: 'all',
        defaultValues: {
            clientId: ''
        }
    });
    const onValid = async (param)=>{
        let { clientId } = param;
        setCreateFailed(false);
        try {
            var _contact_phone;
            await createClientContact({
                clientId,
                firstName: contact.firstName(),
                lastName: contact.lastName(),
                email: contact.email(),
                phone: (_contact_phone = contact.phone()) !== null && _contact_phone !== void 0 ? _contact_phone : undefined
            });
        } catch (unused) {
            setCreateFailed(true);
            return;
        }
        showSuccessToast(t.contactAdded(), ADD_TO_ANOTHER_CLIENT_SUCCESS_TOAST_TEST_ID);
        dismissModal();
    };
    const pickerErrorText = isError ? t.failedToLoadClients() : errors.clientId != null ? t.noClientSelectedError() : undefined;
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: t.title(),
        primaryButtonLabel: t.addButton(),
        secondaryButtonLabel: t.cancelButton(),
        testId: AddToAnotherClientModalTestIds.Root(),
        textAlignCenter: false,
        primaryButtonLoading: isCreating,
        primaryButtonTestId: AddToAnotherClientModalTestIds.AddButton(),
        secondaryButtonTestId: AddToAnotherClientModalTestIds.CancelButton(),
        bi: {
            primaryButton: {
                name: 'add_to_another_client_submit'
            },
            secondaryButton: {
                name: 'add_to_another_client_cancel'
            },
            closeButton: {
                name: 'add_to_another_client_close'
            }
        },
        onPrimaryButtonClick: handleSubmit(onValid),
        onSecondaryButtonClick: dismissModal,
        onCloseButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 24,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    children: t.description({
                        name: contact.fullName()
                    })
                }),
                /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "clientId",
                    rules: {
                        required: true
                    },
                    render: (param)=>{
                        let { field } = param;
                        var _clients_find;
                        return /*#__PURE__*/ _jsx(EnhancedAutocomplete, {
                            bi: {
                                name: 'add_to_another_client_picker'
                            },
                            testId: AddToAnotherClientModalTestIds.ClientAutocomplete(),
                            size: "medium",
                            options: clients,
                            keyField: "clientId",
                            labelField: "name",
                            isLoading: isLoading,
                            disabled: isError,
                            textFieldProps: {
                                bi: {
                                    name: 'add_to_another_client_picker'
                                },
                                label: t.clientLabel(),
                                placeholder: t.clientPlaceholder(),
                                alwaysShrinkLabel: true
                            },
                            selectedOption: (_clients_find = clients.find((client)=>client.clientId === field.value)) !== null && _clients_find !== void 0 ? _clients_find : null,
                            applyOptionChange: (option)=>{
                                var _ref;
                                return field.onChange((_ref = option === null || option === void 0 ? void 0 : option.clientId) !== null && _ref !== void 0 ? _ref : '');
                            },
                            emptySearchText: t.noMatches(),
                            error: pickerErrorText != null,
                            helperText: pickerErrorText
                        });
                    }
                }),
                createFailed && /*#__PURE__*/ _jsx(Box, {
                    testId: AddToAnotherClientModalTestIds.AddError(),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "error",
                        children: t.addError()
                    })
                })
            ]
        })
    });
};
